import React, { useEffect, useMemo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Row, Col, Badge } from 'reactstrap';
import { formValueSelector } from 'redux-form';
import { loadLayouts, loadEmailLayouts, loadTickettypes } from '../../../redux';
import { EmailLayoutsField, PdfLayoutsField } from '../../../components';
import PdfLayoutSampleButton from '../../../components/PdfLayoutSampleButton';
import EmailLayoutPreviewButton from '../../../components/EmailLayoutPreviewButton';
import LayoutDocumentLink from '../../../components/LayoutDocumentLink';
import PdfLayoutLegacyWarning from '../../../components/PdfLayoutLegacyWarning';
import DefaultLayoutsTabContext from './DefaultLayoutsTabContext';
import {
    EMAIL_LAYOUT_KEYS,
    EMAIL_LAYOUT_KEY_TYPES,
    PDF_LAYOUT_KINDS,
    PDF_LAYOUT_TARGETS,
    PDF_KIND_TO_LAYOUT_TYPE,
    resolveSettingsPdfLayoutId,
    pdfLayoutResolutionSource,
    resolveLayoutDisplayName,
    pdfLayoutKindLabel,
    showsPdfTicketLegacyWarning,
} from '../../../services/pdfLayoutSettingsUtils';
import { Utils } from '../../../services';
import { sort_layouts_by_localized_name } from '../../../services/layoutListSort';
import { Tickettype } from '../../../models';

import './DefaultLayoutsForm.scss';

const settingFormSelector = formValueSelector('settingForm');

const DefaultLayoutsForm = ({ setting, pdfOnly = false, emailsOnly = false }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const contextTab = useContext(DefaultLayoutsTabContext);
    const activeTab = pdfOnly ? 'pdfs' : (emailsOnly ? 'emails' : contextTab);
    const storedPdfLayouts = useSelector(state => state.layouts.layouts);
    const emailLayouts = useSelector(state => state.emailLayouts.layouts);
    const { tickettypes } = useSelector(state => state.tickettypes);
    const formEmailLayouts = useSelector(state => settingFormSelector(state, 'email_layouts') || {});
    const formPdfLayouts = useSelector(state => settingFormSelector(state, 'pdf_layouts') || {});

    useEffect(() => {
        if (!emailsOnly) {
            dispatch(loadLayouts());
            dispatch(loadTickettypes());
        }
        if (!pdfOnly)
            dispatch(loadEmailLayouts());
    }, [dispatch, pdfOnly, emailsOnly]);

    const defaultLang = Utils.default_lang();

    const layoutName = (layoutId) => resolveLayoutDisplayName(
        layoutId,
        { pdfLayouts: storedPdfLayouts, emailLayouts },
        i18n.language,
        defaultLang
    );

    const pdfOptions = useMemo(() => {
        const optionsByType = {};

        PDF_LAYOUT_KINDS.forEach(kind => {
            const layoutType = PDF_KIND_TO_LAYOUT_TYPE[kind];
            optionsByType[kind] = {};

            PDF_LAYOUT_TARGETS.forEach(target => {
                optionsByType[kind][target] = sort_layouts_by_localized_name(
                    (storedPdfLayouts || []).filter(layout => (
                        layout.layout_type === 'pdf'
                        && layout.type === layoutType
                        && layout.target === target
                    )),
                    i18n.language,
                ).map(layout => ({
                    key: layout._id,
                    value: layout._id,
                    text: Utils.localized_or_fallback(
                        layout.name,
                        i18n.language,
                        defaultLang
                    ),
                }));
            });
        });

        return optionsByType;
    }, [defaultLang, i18n.language, storedPdfLayouts]);

    const resolutionBadge = (source) => {
        const color = source === 'global' ? 'primary' : (source === 'embedded' ? 'secondary' : 'warning');
        return (
            <Badge color={color} className="ms-2">
                {t(`settings.default_layouts.source_${source}`)}
            </Badge>
        );
    };

    const effectiveLayoutLine = (layoutId, source) => {
        if (!layoutId)
            return null;

        return (
            <div className="mt-2 small text-muted d-flex align-items-center flex-wrap gap-1">
                <span>
                    {t('settings.default_layouts.effective_layout')}
                    {': '}
                    <span className="DefaultLayoutsForm-effective-name">{layoutName(layoutId)}</span>
                    {resolutionBadge(source)}
                </span>
                <LayoutDocumentLink layoutId={layoutId} layoutType="pdf" />
            </div>
        );
    };

    return (
        <div className="DefaultLayoutsForm">
            { !pdfOnly && activeTab === 'emails' && (
                <Card>
                    <CardBody>
                        {EMAIL_LAYOUT_KEYS.map(key => {
                            const configuredId = formEmailLayouts?.[key] || setting?.email_layouts?.[key] || null;
                            const previewLayoutId = configuredId;

                            return (
                                <div key={key} className="DefaultLayoutsForm-row mb-4">
                                    <label className="form-label">
                                        {t(`settings.email_layouts.${key}`)}
                                    </label>
                                    <EmailLayoutsField
                                        emailLayouts={emailLayouts}
                                        types={EMAIL_LAYOUT_KEY_TYPES[key]}
                                        name={`email_layouts.${key}`}
                                        single
                                        placeholder={t('layouts.please_choose_a_layout')}
                                    />
                                    {configuredId && (
                                        <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                                            <EmailLayoutPreviewButton
                                                layoutId={previewLayoutId}
                                                layoutKey={key}
                                                emailLayouts={emailLayouts}
                                                setting={setting}
                                                lang={i18n.language}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </CardBody>
                </Card>
            )}

            {activeTab === 'pdfs' && (
                <Card>
                    <CardBody>
                        {PDF_LAYOUT_KINDS.map(kind => (
                            <div key={kind} className="DefaultLayoutsForm-pdf-kind mb-4">
                                <h4 className="h5">{pdfLayoutKindLabel(kind, {
                                    tickettypes,
                                    lang: i18n.language,
                                    defaultLang,
                                    t,
                                })}</h4>
                                <Row>
                                    {PDF_LAYOUT_TARGETS.map(target => {
                                        const configuredId = formPdfLayouts?.[kind]?.[target]
                                            ?? setting?.pdf_layouts?.[kind]?.[target]
                                            ?? null;
                                        const effectiveId = resolveSettingsPdfLayoutId(setting, kind, target);
                                        const source = pdfLayoutResolutionSource(
                                            setting,
                                            kind,
                                            target,
                                            configuredId
                                        );

                                        return (
                                            <Col key={target} sm={6} md={4} className="mb-3">
                                                <Card className="h-100 inner-card">
                                                    <CardBody>
                                                        <div className="small fw-semibold mb-2">
                                                            {t(`layouts.target_${target}`)}
                                                        </div>
                                                        <PdfLayoutsField
                                                            name={`pdf_layouts.${kind}.${target}`}
                                                            options={pdfOptions[kind]?.[target] || []}
                                                            placeholder={t('layouts.please_choose_a_layout')}
                                                        />
                                                        {effectiveId && effectiveLayoutLine(effectiveId, source)}
                                                        {!effectiveId && showsPdfTicketLegacyWarning(kind, target) && (
                                                            <PdfLayoutLegacyWarning />
                                                        )}
                                                        {!effectiveId && !showsPdfTicketLegacyWarning(kind, target) && (
                                                            <div className="mt-2 small text-muted">
                                                                {t('settings.default_layouts.effective_legacy')}
                                                            </div>
                                                        )}
                                                        {effectiveId && (
                                                            <div className="EmailLayoutsField-actions mt-2">
                                                                <PdfLayoutSampleButton
                                                                    layoutId={effectiveId}
                                                                    storedLayouts={storedPdfLayouts}
                                                                    lang={i18n.language}
                                                                    tickettypeId={kind === 'one_time_pass' ? Tickettype.ONE_TIME_PASS : undefined}
                                                                />
                                                            </div>
                                                        )}
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        ))}
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default DefaultLayoutsForm;
